import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  ContentAnalysisV2Scope,
  ContentCategoriesResponse,
  ContentCategoryCommentsResponse,
  ContentCategoryInsight,
  ContentOverviewV2,
  ContentSentiment,
  ContentTopicDetail,
  ContentTopicsBy,
  ContentTopicsResponse,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'

// Dev only: `REACT_APP_CONTENT_ANALYSIS_V2_MOCK=1` in .env.local answers every
// v2 read from the contract fixtures, so the widgets can be built and checked
// before the sm.back routes are deployed. Builds never set it.
const USE_MOCKS = process.env.REACT_APP_CONTENT_ANALYSIS_V2_MOCK === '1'

const BASE = '/v1_private/content-analysis/v2'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

// The existing content-analysis routes answer `{ success, data }`; the contract
// shows the bare object. Accept either so the frontend does not depend on
// which one the backend settles on.
const unwrap = <T>(body: unknown): T => {
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as { data: T }).data
  }
  return body as T
}

const scopeParams = (scope: ContentAnalysisV2Scope) => ({
  analysisIds: scope.analysisIds?.length
    ? scope.analysisIds.join(',')
    : undefined,
  from: scope.from,
  period: scope.period,
  platform: scope.platform,
  to: scope.to,
})

const mocks = () => import('./mocks')

// The UI counts pages from 1; sm.back's paged reads (topics/detail,
// categories/comments) count from 0, like socialComments.
const toApiPage = (page?: number) =>
  page === undefined ? undefined : Math.max(0, page - 1)

class ContentAnalysisV2ApiService {
  private static _instance: ContentAnalysisV2ApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): ContentAnalysisV2ApiService {
    if (!ContentAnalysisV2ApiService._instance) {
      ContentAnalysisV2ApiService._instance = new ContentAnalysisV2ApiService()
    }
    return ContentAnalysisV2ApiService._instance
  }

  private static async get<T>(
    path: string,
    params: Record<string, unknown>
  ): Promise<T> {
    const response = await ContentAnalysisV2ApiService.instance.client.get(
      `${BASE}${path}`,
      { headers: getHeaders(), params }
    )
    return unwrap<T>(response.data)
  }

  public static async getOverview(
    scope: ContentAnalysisV2Scope
  ): Promise<ContentOverviewV2> {
    if (USE_MOCKS) return (await mocks()).mockOverview(scope)
    return ContentAnalysisV2ApiService.get('/overview', scopeParams(scope))
  }

  public static async getTopics(
    scope: ContentAnalysisV2Scope,
    params: {
      by: ContentTopicsBy
      level?: 'subtopic' | 'topic'
      limit?: number
      topic?: string
    }
  ): Promise<ContentTopicsResponse> {
    if (USE_MOCKS) return (await mocks()).mockTopics(scope, params.by)
    return ContentAnalysisV2ApiService.get('/topics', {
      ...scopeParams(scope),
      ...params,
    })
  }

  public static async getTopicDetail(
    scope: ContentAnalysisV2Scope,
    params: {
      by: ContentTopicsBy
      limit?: number
      name: string
      page?: number
      // Set for a subtopic: the topic it sits under.
      parent?: string
    }
  ): Promise<ContentTopicDetail> {
    if (USE_MOCKS) return (await mocks()).mockTopicDetail(scope, params)
    const detail = await ContentAnalysisV2ApiService.get<ContentTopicDetail>(
      '/topics/detail',
      { ...scopeParams(scope), ...params, page: toApiPage(params.page) }
    )
    return {
      ...detail,
      related: { ...detail.related, page: (detail.related?.page ?? 0) + 1 },
    }
  }

  public static async getCategories(
    scope: ContentAnalysisV2Scope
  ): Promise<ContentCategoriesResponse> {
    if (USE_MOCKS) return (await mocks()).mockCategories(scope)
    return ContentAnalysisV2ApiService.get('/categories', scopeParams(scope))
  }

  public static async getCategoryComments(
    scope: ContentAnalysisV2Scope,
    params: {
      category: string
      limit?: number
      page?: number
      sentiment?: ContentSentiment
    }
  ): Promise<ContentCategoryCommentsResponse> {
    if (USE_MOCKS) return (await mocks()).mockCategoryComments(scope, params)
    const list =
      await ContentAnalysisV2ApiService.get<ContentCategoryCommentsResponse>(
        '/categories/comments',
        { ...scopeParams(scope), ...params, page: toApiPage(params.page) }
      )
    return { ...list, page: (list.page ?? 0) + 1 }
  }

  public static async getCategoryInsight(
    scope: ContentAnalysisV2Scope,
    category: string
  ): Promise<ContentCategoryInsight | null> {
    if (USE_MOCKS) return (await mocks()).mockCategoryInsight(category)
    const insight =
      await ContentAnalysisV2ApiService.get<ContentCategoryInsight | null>(
        '/categories/insight',
        { ...scopeParams(scope), category }
      )
    // An empty body is "no insight yet", the same as an explicit null.
    return insight && typeof insight === 'object' && 'insight' in insight
      ? insight
      : null
  }
}

export { ContentAnalysisV2ApiService }
